import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

/**
 * EmptyState — dashed empty box with icon, title, one caption line and an
 * optional action (UI Design Standards §4.9). Copy is an invitation
 * ("No time entered for this day"), not an apology.
 *
 * Props:
 *   icon         {element}  optional Outlined icon, shown at 24px
 *   title        {node}
 *   description  {node}     optional caption line
 *   action       {node}     optional button
 *   compact      {bool}     no border, less padding (inside tables)
 */
const EmptyState = ({ icon, title, description, action, compact = false, sx }) => {
  const { ft } = useTheme().palette;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.5,
        textAlign: 'center',
        p: compact ? 2 : 3,
        border: compact ? 'none' : `2px dashed ${ft.neutrals.divider}`,
        borderRadius: `${ft.radius.card}px`,
        '& .MuiButton-root': { textTransform: 'none', fontWeight: 500 },
        ...sx,
      }}
    >
      {icon && (
        <Box
          sx={{
            display: 'flex',
            mb: 0.5,
            color: ft.neutrals.textDisabled,
            '& svg': { fontSize: 24 },
          }}
        >
          {icon}
        </Box>
      )}
      {title && (
        <Typography
          component="p"
          sx={{ fontSize: 14, fontWeight: 500, color: ft.neutrals.textBody }}
        >
          {title}
        </Typography>
      )}
      {description && (
        <Typography
          variant="caption"
          component="p"
          sx={{ color: ft.neutrals.textSecondary }}
        >
          {description}
        </Typography>
      )}
      {action && <Box sx={{ mt: 1 }}>{action}</Box>}
    </Box>
  );
};

export default EmptyState;
